/** Provider values are immutable; contextKey identifies the API/session/transport context, not its expiry. */
export interface SessionContext
{
    readonly identity: string;
    readonly contextKey: string;
    /** Absolute milliseconds in the injected clock's timebase; null only when the provider exposes no deadline. */
    readonly expiresAt: number | null;
}

type MaybePromise<T> = T | Promise<T>;

export interface SessionAttempt
{
    readonly generation: number;
    readonly cancelled: Promise<void>;
    isCurrent(): boolean;
}

export type ProviderOutcome<C> =
    | { status: 'ready'; context: C; }
    | { status: 'invalid'; }
    | { status: 'unavailable'; error: unknown; };

export interface SessionFailure
{
    readonly stage: 'provider' | 'persistence' | 'transport' | 'credentials';
    readonly error: unknown;
}

export type SessionOutcome<C> =
    | { status: 'ready'; generation: number; context: C; }
    | { status: 'unavailable'; generation: number; failure: SessionFailure; }
    | { status: 'idle' | 'pending' | 'invalid' | 'expired' | 'signed-out' | 'superseded'; generation: number; };

export interface SessionSnapshot<C>
{
    readonly generation: number;
    readonly status: Exclude<SessionOutcome<C>['status'], 'superseded'>;
    readonly context: C | null;
    readonly failure: SessionFailure | null;
}

export interface LogoutOutcome
{
    readonly status: 'signed-out' | 'superseded';
    readonly generation: number;
    readonly failures: readonly SessionFailure[];
}

export interface SessionCoordinatorOptions<C>
{
    /** Must be synchronous and side-effect free; adapters own renewal scheduling. */
    clock: { now(): number; };
    /** Return context rather than publishing it. Honour the attempt when doing provider-specific work. */
    provider: {
        recover(previous: C | null, attempt: SessionAttempt): MaybePromise<ProviderOutcome<C>>;
        login(attempt: SessionAttempt): MaybePromise<ProviderOutcome<C>>;
        /** Use this captured context, never a mutable current-account credential. Do not clear client storage here. */
        logout(previous: C | null): MaybePromise<void>;
    };
    /** Every returned promise must cover the complete storage operation. The queue is local to this instance. */
    persistence: {
        read(): MaybePromise<C | null>;
        write(context: C): MaybePromise<void>;
        clear(): MaybePromise<void>;
    };
    /** Synchronous publication only; persistence belongs to the serialized hooks above. */
    credentials: { set(context: C | null): void; };
    transport: {
        reset(): void;
        /**
         * Stage materials using the supplied context, without publishing/caching them.
         * Return a synchronous activation callback; only the current unexpired attempt invokes it.
         */
        prepare(context: C, attempt: SessionAttempt): MaybePromise<() => void>;
    };
}

interface Operation<C>
{
    attempt: SessionAttempt;
    fromGeneration: number;
    kind: 'recover' | 'login';
    promise: Promise<SessionOutcome<C>>;
    resolve(outcome: SessionOutcome<C>): void;
}

/**
 * One instance per client session. Provider hooks classify failures and retain UI,
 * health/retry and storage policy. This class never runs or replays a request.
 */
export class SessionCoordinator<C extends SessionContext>
{
    private currentGeneration = 0;
    private status: SessionSnapshot<C>['status'] = 'idle';
    private context: C | null = null;
    private failure: SessionFailure | null = null;
    private pending: Operation<C> | null = null;
    private cancelAttempt: (() => void) | null = null;
    private restoreAllowed = true;
    private candidatePending = false;
    private storageWork: Promise<void> = Promise.resolve();
    private readonly options: SessionCoordinatorOptions<C>;

    public constructor(options: SessionCoordinatorOptions<C>)
    {
        this.options = options;
    }

    public get generation(): number { return this.currentGeneration; }

    public get snapshot(): SessionSnapshot<C>
    {
        this.checkExpiry();
        return { generation: this.generation, status: this.status, context: this.context, failure: this.failure };
    }

    /** A current snapshot, not a credential lease; recheck after any caller-owned asynchronous work. */
    public readiness(): SessionOutcome<C>
    {
        this.checkExpiry();
        if (this.status === 'ready') return { status: 'ready', generation: this.generation, context: this.context! };
        if (this.status === 'unavailable') return { status: 'unavailable', generation: this.generation, failure: this.failure! };
        return { status: this.status, generation: this.generation };
    }

    /** Waits only for an already-running transition; callers still own the operation to run afterwards. */
    public waitForReady(generation = this.generation): Promise<SessionOutcome<C>>
    {
        if (generation !== this.generation && !this.joinsRecovery(generation)) return Promise.resolve(this.superseded(generation));
        const currentGeneration = this.generation;
        this.checkExpiry();
        const pending = this.pending;
        return (pending?.promise ?? Promise.resolve()).then(() => currentGeneration === this.generation
            ? this.readiness() : this.superseded(currentGeneration));
    }

    /** Requests from the generation which started recovery coalesce until the whole transition finishes. */
    public recover(generation = this.generation): Promise<SessionOutcome<C>>
    {
        if (generation !== this.generation && !this.joinsRecovery(generation)) return Promise.resolve(this.superseded(generation));
        const pending = this.pending;
        if (pending)
        {
            this.checkExpiry();
            // Expiry settles this shared operation; joining it must not start another recovery.
            return pending.promise;
        }
        if (this.status === 'signed-out') return Promise.resolve(this.readiness());
        return this.start('recover');
    }

    /** An explicit account replacement also releases logout suppression. */
    public login(): Promise<SessionOutcome<C>> { return this.start('login'); }

    /** Local cancellation is immediate; completion reports persistence/revocation failures without restoring state. */
    public logout(): Promise<LogoutOutcome>
    {
        const previous = this.context;
        const attempt = this.advance();
        this.status = 'signed-out';
        this.context = null;
        this.failure = null;
        this.candidatePending = false;
        this.restoreAllowed = false;
        // Enqueue before invoking external hooks: even a reentrant login must write after this clear.
        const clear = this.persist(() => this.options.persistence.clear());
        const resetFailure = this.reset();
        const failures: SessionFailure[] = resetFailure ? [resetFailure] : [];
        const capture = (stage: SessionFailure['stage'], work: () => MaybePromise<void>): Promise<void> => {
            return Promise.resolve().then(work).then(undefined, error => { failures.push({ stage, error }); });
        };
        return Promise.all([
            capture('persistence', () => clear),
            capture('provider', () => this.options.provider.logout(previous)),
        ]).then(() => ({ status: attempt.isCurrent() ? 'signed-out' : 'superseded', generation: attempt.generation, failures }));
    }

    private joinsRecovery(generation: number): boolean
    {
        return this.pending?.kind === 'recover' && this.pending.fromGeneration === generation;
    }

    private superseded(generation: number): SessionOutcome<C> { return { status: 'superseded', generation }; }

    private advance(): SessionAttempt
    {
        const previous = this.pending;
        this.pending = null;
        this.cancelAttempt?.();
        previous?.resolve(this.superseded(previous.attempt.generation));
        const generation = ++this.currentGeneration;
        let active = true;
        let cancel!: () => void;
        const cancelled = new Promise<void>(resolve => { cancel = resolve; });
        this.cancelAttempt = () => { active = false; cancel(); };
        return { generation, cancelled, isCurrent: () => active && this.generation === generation };
    }

    private start(kind: Operation<C>['kind']): Promise<SessionOutcome<C>>
    {
        const fromGeneration = this.generation;
        const attempt = this.advance();
        let resolve!: Operation<C>['resolve'];
        const promise = new Promise<SessionOutcome<C>>(done => { resolve = done; });
        const operation: Operation<C> = { kind, fromGeneration, attempt, promise, resolve };
        this.pending = operation;
        this.status = 'pending';
        this.failure = null;
        this.candidatePending = false;
        let clear: Promise<void> | undefined;
        if (kind === 'login')
        {
            this.context = null;
            this.restoreAllowed = false;
            clear = this.persist(() => this.options.persistence.clear());
        }
        const failure = this.reset();
        // Observe even an early reset failure without leaving a rejected clear job unhandled.
        void this.run(operation, clear, failure);
        return promise;
    }

    private run(operation: Operation<C>, clear: Promise<void> | undefined, resetFailure: SessionFailure | null): Promise<void>
    {
        const { attempt, kind } = operation;
        let stage: SessionFailure['stage'] = 'persistence';
        return (clear ?? Promise.resolve()).then(() => {
            if (!attempt.isCurrent()) return;
            if (resetFailure) { this.fail(operation, resetFailure); return; }
            if (kind === 'recover' && this.restoreAllowed)
            {
                return this.persist(() => attempt.isCurrent() ? this.options.persistence.read() : null).then(restored => {
                    if (!attempt.isCurrent()) return;
                    this.context = restored;
                    this.restoreAllowed = false;
                });
            }
            return;
        }).then(() => {
            if (!attempt.isCurrent() || this.pending !== operation) return;
            stage = 'provider';
            return kind === 'login' ? this.options.provider.login(attempt) : this.options.provider.recover(this.context, attempt);
        }).then(result => {
            if (!attempt.isCurrent() || this.pending !== operation) return;
            if (!result || (result.status !== 'ready' && result.status !== 'invalid' && result.status !== 'unavailable'))
            {
                throw new Error('Session provider returned an invalid outcome');
            }
            if (result.status === 'unavailable') { this.fail(operation, { stage, error: result.error }); return; }
            if (result.status === 'invalid')
            {
                this.context = null;
                this.status = 'invalid';
                stage = 'persistence';
                return this.persist(() => this.options.persistence.clear()).then(() => {
                    if (attempt.isCurrent()) this.finish(operation);
                });
            }
            this.context = result.context;
            this.candidatePending = true;
            if (this.checkExpiry()) return;
            stage = 'persistence';
            return this.persist(() => attempt.isCurrent() ? this.options.persistence.write(result.context) : undefined).then(() => {
                if (!attempt.isCurrent() || this.checkExpiry()) return;
                stage = 'transport';
                return this.options.transport.prepare(result.context, attempt);
            }).then(activate => {
                if (!attempt.isCurrent() || this.pending !== operation || this.checkExpiry()) return;
                if (typeof activate !== 'function') throw new Error('Session transport preparation returned no activation callback');
                this.publish(attempt, activate);
                if (!attempt.isCurrent() || this.checkExpiry()) return;
                stage = 'credentials';
                this.publish(attempt, () => this.options.credentials.set(result.context));
                if (!attempt.isCurrent() || this.checkExpiry()) return;
                this.status = 'ready';
                this.finish(operation);
            });
        }).catch(error => {
            if (attempt.isCurrent()) this.fail(operation, { stage, error });
        });
    }

    private publish(attempt: SessionAttempt, commit: () => void): void
    {
        try { commit(); }
        finally
        {
            // A hook can reenter logout/login, then finish publishing old state or throw.
            // Clear in this stack: a replacement can only activate on a later Promise turn.
            // Deferring this cleanup could instead erase the replacement's ready context.
            if (!attempt.isCurrent()) this.reset();
        }
    }

    private fail(operation: Operation<C>, failure: SessionFailure): void
    {
        this.status = 'unavailable';
        this.failure = failure;
        this.reset();
        if (operation.attempt.isCurrent()) this.finish(operation);
    }

    private finish(operation: Operation<C>): void
    {
        if (this.pending !== operation) return;
        this.pending = null;
        this.candidatePending = false;
        operation.resolve(this.readiness());
    }

    private checkExpiry(): boolean
    {
        if (this.status !== 'ready' && !(this.status === 'pending' && this.candidatePending)) return false;
        const expiry = this.context?.expiresAt;
        if (expiry === null || (expiry !== undefined && Number.isFinite(expiry) && this.options.clock.now() < expiry)) return false;
        const pending = this.pending;
        this.pending = null;
        this.status = 'expired';
        this.candidatePending = false;
        this.cancelAttempt?.();
        this.reset();
        pending?.resolve({ status: 'expired', generation: pending.attempt.generation });
        return true;
    }

    /** Clear both surfaces even when one hook fails. The readiness gate remains closed. */
    private reset(): SessionFailure | null
    {
        let failure: SessionFailure | null = null;
        try { this.options.credentials.set(null); }
        catch (error) { failure = { stage: 'credentials', error }; }
        try { this.options.transport.reset(); }
        catch (error) { failure ??= { stage: 'transport', error }; }
        return failure;
    }

    /** All storage work, including unconditional clears, is ordered after already-started writes. */
    private persist<T>(work: () => MaybePromise<T>): Promise<T>
    {
        const result = this.storageWork.then(work);
        this.storageWork = result.then(() => undefined, () => undefined);
        return result;
    }
}
